import moment from "moment";
import HasLabel from "./HasLabel";
import { Order, OrderStatus } from "./Order";
import TrackedObject from "./TrackedObject";
import TextUtils from "@/util/TextUtils";
import { Patient } from "@/models/Patient";
import { Program } from "@/models/Program";
import { Claim } from "./Claim";
import { Prescriber } from "@/models/Prescriber";
import { DeliveryCode } from '@/models/DeliveryCode'
import { DrugSource } from "@/models/Drug/DrugProduct";
import { ReminderSupression } from "./ReminderSuppression";
import { RxDiagnosis } from "@/models/GSDD";

export interface Prescription extends TrackedObject {
    id?: number;
    storeID: number;
    rxNumber: number;
    rfNumber?: number;
    imageID?: number;
    escriptID?: number;
    // Tells the save this is an additional prescription typed from an eScript that already has one, so it
    // inserts keeping the link instead of resolving to — and overwriting — the eScript's existing Rx.
    createNewFromEscript?: boolean;
    programTransferId?: number;
    directions: string;
    daySupply: number;
    quantity: number;
    dispensed: number;
    refills: number;
    originCode: OriginCode;
    dawCode: DAWCode;
    status: RefillStatusCode;
    subStatus: number | null;
    subStatusName?: string | null;
    memo: string;
    programName: string;
    storeName: string;
    lotNumber: string;
    drugExpirationDate: Date | null;
    totalPrice: number | null;
    cost: number | null;
    patientPay: number | null;
    tax: number | null;
    deliveryCode: DeliveryCode;
    deliveryOption: string;
    packageID: number;
    drugSource: DrugSource;
    patientID: number;
    prescriberID: number;
    supervisingPhysicianId?: number | null;
    prescriberName?: string;
    programID?: number;
    orderId: number;
    originalRxDate: Date;
    fillDate: Date;
    writtenDate: Date;
    expirationDate: Date | null;
    effectiveDate: Date | null;
    drugsFilledDate: Date | null;
    /** Latest label print for this fill. Only usp_Prescription_GetById supplies it. */
    labelPrinted?: Date | null;
    annotations: string;
    drugName: string;
    ndc11?: string;
    productNameShort: string;
    lastShippedDate?: Date | string | null;
    lastShippedIsEquivalent?: boolean | null;
    pV1ID: number;
    pV2ID: number | null;
    pv1: Date;
    pv1By: string;
    pv2: Date;
    pv2By: string;
    // True when the PV2 verification behind pV2ID was cancelled — a non-null pV2ID is not proof of
    // verification. Mirrors the server's unverified rule; absent on scripts not loaded via the order.
    pv2Cancelled?: boolean;
    // Set when the line was pulled from the print queue without printing a label. Not the same as
    // removed from the order (that is a hard delete), but the line is skipped by shipping, revenue
    // recognition and the delivery log. Absent on scripts not loaded via the order.
    removedFromPrintQueue?: Date | null;
    removedFromPrintQueueBy?: string;
    dispenseErrorId?: number;
    dispenseErrorStatus?: string;
    canBeFilled: boolean;
    totalDispensed: number;
    totalAllowedToDispense: number;
    priceScheduleID?: number;
    deaClassificationID?: number;
    isPV1Required?: boolean;
    requiresPV1: boolean;
    isAvailableForAutorefill: boolean;
    isAutoRefillEnrolled?: boolean;

    orders: Array<Order>;
    patient: Patient;
    prescriber: Prescriber;
    claims: Array<Claim>;
    program: Program;
    suppression: ReminderSupression;

    serialNumber: string;
    partnerFillNumber: string;
    originPharmacyId?: number;
    transferMyRxId?: string;
    isUrgent: boolean;
    needsRefrigeration: boolean;
    treatmentTypeCode?: TreatmentType | null;
    nextTouchDate: Date | null;
    nextTouchDateManualOverride: boolean;
    compoundPresetID?: number | null;

    dispensingItems?: PrescriptionDispenseItem[];

    estimatedCost: number;
    isExistingFill?: boolean;

    aspnRxID?: number | null;

    // ICD10 diagnosis codes associated with this Rx (gated by the EnableRxLevelDiagnosis config).
    // Sent with the save payload as the authoritative desired set; hydrated on load/save response.
    diagnosisCodes?: RxDiagnosis[];

    // Server-populated lowercase shadow of the computed `rxID` getter. The API serializes the
    // billed fill's identity here; the RxId setter server-side can re-derive the fill from it.
    // Declared so callers can clear it in a build-time-safe way (see prepareNewRxFromBilled),
    // rather than reaching the untyped field through an `as any` cast.
    rxId?: string;
}

export interface PrescriptionDispenseItem {
    lotNumber?: string;
    serialNumber?: string;
    expirationDate?: Date | null;
    quantity?: number | null;
    gtin?: string;
    source?: DispenseItemSource | null;
}

export enum DispenseItemSource {
    Manual = 0,
    DataMatrix = 1,
    UPC = 2,
    Parata = 3,
    // Reported by a partner through the External API rather than captured at a fill station. It was
    // never reconciled against stock this pharmacy holds, which is why it reads differently on PV2.
    External = 4
}

export interface FaxForm {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    fax: string;
    dea: string;
    pharmacist: string;
    verbalTransfer: boolean | null;
    note: string;
    alternateDrug: string;
    rxID: string;
    patientID: number;
    prescriberID: number;
    packageID: number;
    drugSource: number;
    storedPharmacyId: number;
    patientPlanIds: number[];
    storeID: number;
    appendPrescriptionImage: boolean;
    imageIds?: number[] | null;
    excludeRefillRequestsFromNotes: boolean;
    programID?: number | null;
    rxTransferReasonId?: number | null;
    // Which number the operator picked in the fax dialog. Only the prior-authorization dialogs send
    // it; the server uses it to record who was actually contacted on the outreach log.
    faxNumberSource?: string | null;
    // Set only once the operator has confirmed the reversal prompt; the server will not reverse without it.
    reverseActiveClaims?: boolean;
    // The Rx Memo the operator edited on the screen that started the transfer. Only Accept and Transfer
    // sends it, because only PV1 transfers out of an editable memo box without saving the prescription
    // first, and only when the box actually differs from what was loaded. Null leaves the stored memo
    // alone; an empty string clears it.
    rxMemo?: string | null;
}

export class FaxForm {
    constructor() {
        this.name = '';
        this.address = '';
        this.city = '';
        this.state = '';
        this.zip = '';
        this.phone = '';
        this.fax = '';
        this.dea = '';
        this.pharmacist = '';
        this.verbalTransfer = null;
        this.note = '';
        this.alternateDrug = '';
        this.storedPharmacyId = 0;
        this.patientPlanIds = [];
        this.storeID = 0;
        this.appendPrescriptionImage = false;
        this.imageIds = [];
        this.excludeRefillRequestsFromNotes = false;
    }
}

export enum OriginCode {
    NotSpecified,
    Written,
    Phone,
    Escript,
    Fax,
    Transfer,
    Custom
}

export enum TreatmentType {
    NotForOpioidDependency = 1,
    OpioidDependencyTreatment = 2,
    CancerTreatmentPain = 3,
    PalliativeCare = 4,
    EndOfLifeHospice = 5,
    PregnantWithOpioidRx = 6,
    AcutePainWithChronicOpioid = 7,
    ActiveOpioidTaper = 8,
    PainManagementContract = 9,
    AcuteOpioidTherapy = 10,
    ChronicOpioidTherapy = 11,
    Other = 99,
}

export enum DAWCode {
    NotSpecified,
    Prescriber,
    Patient,
    Pharmacy,
    GenericNotStocked,
    DispenseAsGeneric,
    Override,
    SubstitutionNotAllowed,
    SubstitutionOkNoGeneric,
    Other
}

export enum RefillStatusCode {
    Refillable = 0,
    AutoRefillable = 1,
    NonRefillable = 2,
    Suspended = 3,
    TransferOut = 4,
    TransferIn = 5,
    OnHold = 6,
    Canceled = 7,
    Expired = 8,
    Void = 9,
    AwaitingInsurance = 10,
    Discontinued = 11,
}

export const RxDeniedSubStatusId = 14;

/**
 * Whether a prescription in this status may be cashed out. Mirrors
 * Prescription.NonBillableStatusReason on the server, which is the authority — this only spares
 * the technician a round trip that would come back rejected.
 *
 * Takes a bare status rather than a Prescription so the edit screen can gate its Cash Out button
 * on the status the technician has currently selected. The prescription model carries the last
 * status the server returned, which goes stale the moment the status dropdown changes, and gating
 * on that left the button disabled after a hold was cleared until the page was reloaded.
 */
export function isBillableStatus(status: RefillStatusCode | null | undefined): boolean {
    switch (status) {
        case RefillStatusCode.Suspended:
        case RefillStatusCode.TransferOut:
        case RefillStatusCode.OnHold:
        case RefillStatusCode.Canceled:
        case RefillStatusCode.Expired:
        case RefillStatusCode.Void:
        case RefillStatusCode.Discontinued:
            return false;
        default:
            return true;
    }
}

export class Prescription extends HasLabel {
    constructor(obj?: Prescription) {
        super();
        this.requiresPV1 = false;
        this.fillDate = moment().utc(true).toDate();
        this.status = RefillStatusCode.Refillable;
        this.deliveryCode = DeliveryCode.Standard;
        this.patientID = 0;
        this.storeID = 0;
        this.packageID = 0;
        this.prescriberID = 0;
        this.isAvailableForAutorefill = false;
        this.isAutoRefillEnrolled = false;
        this.needsRefrigeration = false;
        this.programID = 0;
        this.program = new Program();
        this.imageID = undefined; // declare so Vue 2 tracks in-place imageID/escriptID updates pre-save
        this.escriptID = undefined;
        // False on every fresh draft, so the flag cannot outlive the Save-and-New that set it.
        this.createNewFromEscript = false;

        if (obj) Object.assign(this, obj);
    }

    toString(): string {
        if (!this.rxNumber) return '<div class="font-weight-light">Store-Rx-Rf</div>';

        const parts = [];

        if (this.rxNumber) {
            parts.push(`<strong>${this.rxID}</strong>`);
        }

        if (this.productNameShort) {
            parts.push(TextUtils.escapeHtml(this.productNameShort));
        }

        if (this.prescriberName) {
            parts.push(`Prescriber: ${TextUtils.escapeHtml(this.prescriberName)}`);
        }

        if (this.writtenDate) {
            parts.push(`Written: ${moment(this.writtenDate).format("YYYY-MM-DD")}`);
        }

        if (this.patientID) {
            parts.push(`Patient Id: ${this.patientID}`);
        }

        return parts.join(', ');
    }

    // This has a computed getter but might be populated from the backend, an empty setter allows the object to be assigned without error
    set rxID(_:string){}

    get rxID(): string {
        return Prescription.createRxId(this.storeID, this.rxNumber, this.rfNumber);
    }

    get isActive(): boolean {
        return !(this.status == RefillStatusCode.TransferOut || this.isVoid) && !this.isExpired;
    }

    /**
     * Whether a cash out may be attempted against this prescription. Mirrors
     * Prescription.NonBillableStatusReason on the server, which is the authority — this getter
     * only spares the technician a round trip that would come back rejected.
     * Deliberately stricter than isActive, which is read widely for display and has to keep
     * treating a held script as live.
     */
    get isBillable(): boolean {
        return isBillableStatus(this.status);
    }

    /**
     * Whether the "Return to Asembia" action is available. Mirrors the
     * terminal-state rejects in PrescriptionController.ReturnToAsembia
     * (TransferOut / Void / Canceled) so the button isn't shown for an Rx the API
     * would 400. isActive already excludes TransferOut and Void; Canceled (status 7)
     * is the gap that otherwise slipped through and produced a misleading error toast.
     */
    get isReturnableToAsembia(): boolean {
        return this.isActive && this.status != RefillStatusCode.Canceled;
    }

    /**
     * Indicates if prescription is Void or RxDenied.
     */
    get isVoid(): boolean {
        return this.status == RefillStatusCode.Void
            || this.status == RefillStatusCode.Discontinued
            || (this.status == RefillStatusCode.OnHold && this.subStatus == RxDeniedSubStatusId);
    }

    /**
     * Indicates if the program allows the prescription to refill even if previous refill has not been shipped.
     */
    get programAllowsRefillsIfUnshipped(): boolean {
        if (!this.program?.flags.length) return false;

        return this.program.flags.some(f => f.value && f.name == "Allow Refills if Unshipped");
    }

    get isRefillable(): boolean {
        //const hasBeenPV2 = !!this.pV2ID;
        const rx30ScriptUpdatedByCli = (this.createdBy == 'Rx30' && this.updatedBy == 'CPHub/CLI');

        return this.canBeFilled && this.isRefillableStatus
            && (this.hasBeenShipped || this.programAllowsRefillsIfUnshipped || this.updatedBy == 'Rx30' || rx30ScriptUpdatedByCli);
        // && ((this.hasBeenShipped && hasBeenPV2) || this.fillDate < moment('04/01/2021').toDate());
    }

    get isRefillableStatus(): boolean {
        return (this.status == RefillStatusCode.Refillable || this.status == RefillStatusCode.AutoRefillable
            || this.status == RefillStatusCode.TransferIn) && this.isActive;
    }

    get isExpired(): boolean {
        if (!this.expirationDate) return false;

        const expMoment = moment(this.expirationDate);
        return moment().isAfter(expMoment, 'days');
    }

    /**
     * Indicates if the prescription is in a order that has already been shipped.
     */
    get hasBeenShipped(): boolean {
        return this.orders?.some(o => o.status == OrderStatus.Shipped) ?? false;
    }

    /**
     * True once the fill has physically left the pharmacy, including an order that
     * shipped and came back. Broader than hasBeenShipped, which matches only the
     * Shipped status; a will-call fill awaiting pickup is deliberately excluded.
     */
    get hasBeenShippedOrReturned(): boolean {
        return this.orders?.some(o => o.status == OrderStatus.Shipped || o.status == OrderStatus.ReturnToSender) ?? false;
    }

    get hasBeenVerified(): boolean {
        return (this.pV2ID ?? 0) > 0;
    }

    get hasFullId(): boolean {
        return !!(this.storeID && this.rxNumber && (this.rfNumber || this.rfNumber == 0));
    }

    get statusName(): string {
        if (this.isExpired) return "Expired";
        return TextUtils.camelCaseToNormal(RefillStatusCode[this.status]);
    }

    get isCashedOut(): boolean {
        return !!(this.priceScheduleID);
    }

    get isRx30BeforeApril(): boolean {
        const myMoment = moment;
        return (this.status != RefillStatusCode.OnHold &&
            myMoment(this.fillDate).isSameOrBefore(myMoment("2021-04-01")) &&
            this.createdBy == 'Rx30')
            || (this.status != RefillStatusCode.OnHold &&
                (this.updatedBy == 'Rx30' || this.updatedBy == 'CPHub/CLI') &&
                this.memo == "Transferred from Rx30 post-launch");
    }

    public static createRxId(storeID: number, rxNumber: number, rfNumber: number | undefined): string {
        return `${storeID || ''}-${rxNumber || ''}-${rfNumber == undefined ? '' : (rfNumber || 0)}`;
    }

    get remainingQuantity(): number {
        if (!this.rxNumber || this.rfNumber == 0) return (this.quantity * ((this.refills || 0) + 1));

        const res = this.totalAllowedToDispense - (this.totalDispensed || 0);
        return Number(res.toFixed(2));
    }

    // Display form for list columns. Unlike remainingQuantity it clamps over-dispensed scripts to
    // 0 and shows a dash when either total is absent, rather than reading as a real quantity.
    get remainingQuantityDisplay(): string {
        if (this.totalAllowedToDispense == null || this.totalDispensed == null)
            return '—';
        return String(Math.max(0, Number((this.totalAllowedToDispense - this.totalDispensed).toFixed(2))));
    }

    get isCII(): boolean {
        return this.deaClassificationID == 1 || this.deaClassificationID == 2;
    }

}
